import React, { PureComponent, RefObject, createRef } from 'react';
import { $location, $document, classSet } from '@umu/umu-dom';
import * as imageUtil from '@umu/umu-image-handle';
import If from '../if';
import UMUBg from '../UMUBg/UMUBg';
import { A } from '../frameA/frameA';
import Swipe from '../fullImgGallery/swipe';

export interface ICarouselItem {
    title: string;
    url: string;
    imgUrl: string;
}

interface IProps {
    ratio?: number;
    clsName?: string;
    data: ICarouselItem[];
    duration?: number;
    autoPlay?: boolean;
    noAppendWidth?: boolean; // 图片是否不需要添加默认宽度
    showActiveOpera?: boolean; // 左右切换按钮
    onItemClick?(url: string): void;
}

interface IState {
    playId: any;
    width: number;
    height: number;
    currentIndex: number;
    data: ICarouselItem[];
}

const bodyWidth = Math.max($document.body['scrollWidth'], $document.body['offsetWidth']);

const defaultRatio = 16 / 7;

import './carousel.scss';

/**
 * @description 轮播图
 * @author Kody
 */
export class Carousel extends PureComponent<IProps, IState> {
    public static defaultProps: IProps = {
        ratio: defaultRatio,
        clsName: '',
        autoPlay: true,
        data: [],
        duration: 5000,
    };

    public static getDerivedStateFromProps = (nextProps: IProps, prevState: IState) => {
        if (JSON.stringify(nextProps.data) !== JSON.stringify(prevState.data.slice(0, prevState.data.length - 1))) {
            return {
                data: [...nextProps.data, nextProps.data[0]],
            };
        }
        return null;
    };

    public swiper: Swipe | null;
    public carouselWrap: RefObject<HTMLDivElement>;
    public carrouselSlides: RefObject<HTMLDivElement>;
    public DEFAULT_WIDTH = bodyWidth || 0;
    public devicePixelRatio = window.devicePixelRatio || 2;
    constructor(props: IProps) {
        super(props);
        this.DEFAULT_WIDTH = bodyWidth || 0;
        if (this.DEFAULT_WIDTH === 600) {
            this.DEFAULT_WIDTH = 600 - 2;
        }
        this.state = {
            playId: 0,
            currentIndex: 0,
            data: [...props.data, props.data[0]],
            width: this.DEFAULT_WIDTH,
            height: Math.round(this.DEFAULT_WIDTH / (props.ratio || defaultRatio)),
        };
        this.swiper = null;
        this.carouselWrap = createRef();
        this.carrouselSlides = createRef();
    }

    public componentDidMount = () => {
        this.init();
        window.addEventListener('resize', this.initialSize);
    };

    public componentWillUnmount = () => {
        clearTimeout(this.state.playId);
        if (this.swiper) {
            this.swiper.destroy();
        }
        window.removeEventListener('resize', this.initialSize);
    };

    public init = () => {
        this.initialSize();
        if (this.props.autoPlay) {
            this.play();
        }
    };

    public initialSize = () => {
        this.DEFAULT_WIDTH = bodyWidth || 0;
        if (this.DEFAULT_WIDTH === 600) {
            this.DEFAULT_WIDTH = 600 - 2;
        }

        const carouselWrap = this.carouselWrap.current;
        if (!carouselWrap) {
            return;
        }

        const width = carouselWrap.offsetWidth || this.DEFAULT_WIDTH;
        const height = Math.round(width / (this.props.ratio || defaultRatio));

        // 获取轮播宽度
        this.setState({
            width,
            height,
        });

        this.initialSwipe();
    };

    public initialSwipe = () => {
        this.swiper = new Swipe('.carrousel-item');
        this.swiper.onSwipeLeft(() => {
            this.nextSlide();
        });
        this.swiper.onSwipeRight(() => {
            this.prevSlide();
        });
    };

    public getClassName = (data: IProps) => {
        const classObj: { [name: string]: any } = {
            'carrousel-wrap': true,
        };
        if (data && data.clsName) {
            classObj[data.clsName] = true;
        }
        return classSet(classObj);
    };

    // 无限轮播
    public unlimitedCarousel = () => {
        const data = this.state.data;
        const carrouselSlides = this.carrouselSlides.current;
        if (!carrouselSlides) {
            return;
        }
        if (this.state.currentIndex === 0) {
            carrouselSlides.setAttribute('style', 'transition: none');
        }
        if (this.state.currentIndex === data.length - 1) {
            setTimeout(() => {
                carrouselSlides.setAttribute('style', 'transition: none');
            }, 500);
            setTimeout(() => {
                this.setState(
                    {
                        currentIndex: 0,
                    },
                    () => {
                        carrouselSlides.setAttribute('style', 'transition: left .5s ease, right .5s ease');
                    },
                );
            }, 600);
        }
    };

    public play = () => {
        clearInterval(this.state.playId);
        const imgCnt = this.state.data.length;
        if (!imgCnt) {
            // 防止为0
            return;
        }

        const playId = setInterval(() => {
            const nextIndex = (this.state.currentIndex + 1) % imgCnt;
            this.setState(
                {
                    currentIndex: nextIndex,
                },
                this.unlimitedCarousel,
            );
        }, this.props.duration);

        this.setState({
            playId,
        });
    };

    /**
     * 切换到相应轮播
     */
    public goToSlide = (num: number) => {
        this.setState({
            currentIndex: +num,
        });
        if (this.props.autoPlay) {
            this.play();
        }
    };

    /**
     * 上一张
     */
    public prevSlide = () => {
        const imgCnt = this.state.data.length;
        let currentIndex = this.state.currentIndex;
        if (currentIndex - 1 < 0) {
            currentIndex = imgCnt;
        }
        const num = Math.abs(--currentIndex) % imgCnt;
        this.goToSlide(num);
    };

    /**
     * 下一张
     */
    public nextSlide = () => {
        const imgCnt = this.state.data.length;
        let currentIndex = this.state.currentIndex;
        const num = Math.abs(++currentIndex) % imgCnt;
        this.goToSlide(num);
    };

    public getSlidesStyle = () => {
        return {
            left: -this.state.currentIndex * this.state.width + 'px',
            width: this.state.width * this.state.data.length + 'px',
        };
    };

    public onItemClick = (url: string) => {
        if (!url) {
            return;
        }
        if (this.props.onItemClick) {
            this.props.onItemClick(url);
        } else {
            $location.href = url;
        }
    };

    public renderImgs = () => {
        const itemStyle = {
            width: this.state.width + 'px',
        };
        const isNoAppendWidth = this.props.noAppendWidth;
        return (this.state.data || []).map((item, index) => {
            const bgSrc = isNoAppendWidth
                ? item.imgUrl
                : imageUtil.appendWidth(item.imgUrl, Math.round(this.state.width * this.devicePixelRatio));
            return (
                <div className="carrousel-item" key={index} style={itemStyle}>
                    <A href={item.url} onClick={() => this.onItemClick(item.url)}>
                        <>
                            <UMUBg
                                clsName="carrousel-img"
                                bgSrc={bgSrc}
                                style={{ height: this.state.height }}
                            />
                            {item.title ? (
                                <div className="carrousel-title">
                                    <p>{item.title}</p>
                                </div>
                            ) : null}
                        </>
                    </A>
                </div>
            );
        });
    };

    public renderIndicator = () => {
        const data = this.state.data;
        if (!data.length) {
            return null;
        }
        return (data || []).map((_item, index) => {
            if (index === data.length - 1) {
                return null;
            }
            const isActive =
                this.state.currentIndex === index || (index === 0 && this.state.currentIndex === data.length - 1);
            return (
                <li
                    key={index}
                    className={classSet({
                        'indicator-item': true,
                        'active': isActive,
                    })}
                    onClick={this.goToSlide.bind(this, index)}
                ></li>
            );
        });
    };

    public renderOpera = () => {
        const data = this.state.data;
        if (!this.props.showActiveOpera || data.length <= 1) {
            return null;
        }
        return (
            <>
                <button className="active-opera prev" onClick={this.prevSlide}>
                    <i className="iconfont icon-arrow-right left"></i>
                </button>
                <button className="active-opera next" onClick={this.nextSlide}>
                    <i className="iconfont icon-arrow-right"></i>
                </button>
            </>
        );
    };

    public render = () => {
        const props = this.props;
        const data = this.state.data;
        if (!data.length) {
            return null;
        }

        return (
            <div className={this.getClassName(props)} ref={this.carouselWrap}>
                <div ref={this.carrouselSlides} className="carrousel-slides" style={this.getSlidesStyle()}>
                    {this.renderImgs()}
                </div>
                <If condition={data.length > 1}>
                    <ul className="carrousel-indicator">{this.renderIndicator()}</ul>
                </If>
                {this.renderOpera()}
            </div>
        );
    };
}
